<script setup>
// SiteUp: bolha da chegada de lead vindo de anúncio.
//
// O bridge despeja o anúncio inteiro como markdown cru no fio — medido em
// produção: 3.497 mensagens em 8 contas, média de 765 caracteres e máximo de
// 1.968. O atendente abre a conversa e vê uma parede de texto no lugar do que
// a pessoa escreveu.
//
// Aqui o anúncio vira um card compacto (o mesmo desenho já aprovado no
// AdArrivalNotice) e a FALA DO CLIENTE — que vem dentro da mesma mensagem,
// depois de "MENSAGEM DO CLIENTE:" — sobe para o lugar de destaque.
//
// Se o parser não reconhecer nada aproveitável ele devolve null e o
// componentToRender nem escolhe esta bolha: a mensagem segue como texto comum.
// Nunca escondemos conteúdo por falha de reconhecimento.
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './Text/FormattedContent.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';
import { parseAdMessage } from 'dashboard/helper/adMessage';

const { content, contentAttributes, conversationId } = useMessageContext();
const store = useStore();

const expanded = ref(false);
const showTech = ref(false);

// SiteUp: quando o fork WAHA manda o anúncio estruturado, quem já normalizou os
// 32 campos (com todos os apelidos de nome que cada bridge usa) foi o
// CtwaReferralProcessor, na conversa. Reler o externalAdReply cru aqui seria uma
// segunda implementação da mesma normalização para esquecer de manter.
const ctwaAttrs = computed(() => {
  if (!contentAttributes?.value?.externalAdReply) return null;

  const conversation = store.getters.getConversationById?.(
    conversationId?.value
  );
  const attrs = conversation?.custom_attributes || {};
  const structured = {
    title: attrs.ctwa_headline,
    description: attrs.ctwa_body,
    link: attrs.ctwa_source_url,
    adId: attrs.ctwa_source_id,
    clickId: attrs.ctwa_clid,
  };
  return Object.values(structured).some(Boolean) ? structured : null;
});

// Estruturado tem prioridade; o parser de texto continua servindo o despejo cru
// (as mensagens já no banco e bridges fora do caminho estruturado). A fala do
// cliente vem do próprio content quando o anúncio veio estruturado — ali o
// content JÁ É só o que a pessoa escreveu.
const ad = computed(() => {
  const structured = ctwaAttrs.value;
  if (structured) return { ...structured, customerMessage: content.value };

  return parseAdMessage(content.value) || {};
});

const headline = computed(
  () => ad.value.title || ad.value.description?.split('\n')[0] || ''
);
const hasDetails = computed(() =>
  Boolean(ad.value.description || ad.value.link || ad.value.adId)
);
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="ad-message">
    <div class="flex flex-col gap-3">
      <div class="rounded-xl bg-n-alpha-2">
        <button
          type="button"
          data-testid="ad-message-toggle"
          class="flex gap-2.5 items-center p-2.5 w-full text-left rounded-xl transition-colors hover:bg-white/[0.03]"
          :aria-expanded="expanded"
          :disabled="!hasDetails"
          @click="expanded = !expanded"
        >
          <span
            class="flex justify-center items-center rounded-[10px] size-7 shrink-0 bg-white/[0.06]"
          >
            <Icon class="size-3.5 text-n-blue-9" icon="i-ri-megaphone-line" />
          </span>
          <span class="flex flex-col flex-1 min-w-0">
            <span
              class="text-[13px] font-bold leading-tight tracking-tight text-n-slate-12"
            >
              {{ $t('CONVERSATION.AD_ARRIVAL.TITLE_AD') }}
            </span>
            <span
              v-if="headline"
              class="mt-0.5 text-xs leading-snug text-n-slate-11"
              :class="expanded ? 'whitespace-normal' : 'truncate'"
            >
              {{ headline }}
            </span>
          </span>
          <Icon
            v-if="hasDetails"
            class="transition-transform duration-150 size-4 shrink-0 text-n-slate-11"
            :class="{ 'rotate-180': expanded }"
            icon="i-ri-arrow-down-s-line"
          />
        </button>

        <div v-if="expanded && hasDetails" class="px-2.5 pb-2.5">
          <p
            v-if="ad.description"
            class="m-0 text-xs leading-snug whitespace-pre-line text-n-slate-11"
          >
            {{ ad.description }}
          </p>
          <div class="flex flex-wrap gap-3 items-center mt-2">
            <a
              v-if="ad.link"
              :href="ad.link"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex gap-1 items-center text-xs font-semibold text-n-blue-9 hover:underline"
            >
              <Icon class="size-3" icon="i-ri-external-link-line" />
              {{ $t('CONVERSATION.AD_ARRIVAL.OPEN_SOURCE') }}
            </a>
            <button
              v-if="ad.adId"
              type="button"
              class="text-xs font-semibold text-n-slate-11 hover:text-n-slate-12"
              :aria-expanded="showTech"
              @click="showTech = !showTech"
            >
              {{ $t('CONVERSATION.AD_ARRIVAL.TECH_DETAILS') }}
            </button>
          </div>
          <div
            v-if="showTech && ad.adId"
            class="flex gap-2 items-baseline pt-2 mt-2 text-xs border-t border-n-weak"
          >
            <span class="text-n-slate-11 shrink-0">
              {{ $t('CONVERSATION.AD_ARRIVAL.SOURCE_ID') }}
            </span>
            <span class="flex-1 min-w-0 text-right truncate text-n-slate-12">
              {{ ad.adId }}
            </span>
          </div>
          <!-- SiteUp: o click id é o que a Meta usa para casar a conversa com a
               conversão do anúncio. Já era capturado e nunca aparecia. -->
          <div
            v-if="showTech && ad.clickId"
            class="flex gap-2 items-baseline pt-2 mt-2 text-xs border-t border-n-weak"
          >
            <span class="text-n-slate-11 shrink-0">
              {{ $t('CONVERSATION.AD_ARRIVAL.CLICK_ID') }}
            </span>
            <span class="flex-1 min-w-0 text-right truncate text-n-slate-12">
              {{ ad.clickId }}
            </span>
          </div>
        </div>
      </div>

      <FormattedContent
        v-if="ad.customerMessage"
        :content="ad.customerMessage"
      />
    </div>
  </BaseBubble>
</template>
